import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatMenu } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { takeUntil } from 'rxjs';
import { BaseComponent } from '../../../../core/components/base/base.component';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { PatientRiskItemService } from '../../../../core/services/patient-risk-item.service';
import { PatientService } from '../../../../core/services/patient.service';
import { RiskItemService, groupRiskItemsByHospital, riskCategorySummary, riskHospitalGroupKey } from '../../../../core/services/risk-item.service';
import { RISK_CATEGORIES, RISK_SEVERITIES, RISK_STATUSES, riskCategoryLabel, riskLocationLabel, riskSeverityLabel } from '../../../../shared/components/risk-suggestion-list/risk-suggestion.model';
import { RiskItemDetailDialogComponent } from './risk-item-detail-dialog.component';

/** One category's rows inside one facility's section. */
export interface RiskCategorySection {
  category: string;
  label: string;
  items: any[];
}

/** One facility's section of the register — the on-screen twin of the PDF's per-hospital table. */
export interface RiskHospitalSection {
  key: string;
  title: string;
  /** `hospitalId` was NULL and the section is held together by `hospitalName` alone. See G6 below. */
  nameOnly: boolean;
  summary: string;
  total: number;
  categories: RiskCategorySection[];
}

/**
 * The facility risk register — G2/G5/G6, and the caller of H's export.
 *
 * Grouped BY HOSPITAL THEN BY CATEGORY, which is the shape the exported PDF takes (H3) and the
 * shape D7 exists to produce: "17 consumable stock-outs at Hospital X this month" is a
 * conversation with a hospital; sixty loose sentences is a shrug. The grouping is done by
 * `groupRiskItemsByHospital` in the service so the screen and the document can never disagree
 * about which rows belong to which facility.
 *
 * `@Input() filterValue` + `applyFilter()` follow `task-list.component.ts:74`, and the write path
 * follows the same file's precedent: reads come from the GROUP-scoped `RiskItemService`, writes go
 * through `PatientRiskItemService.updateRiskItem` — exactly as `task-list` reads `TaskService` and
 * writes `PatientTaskService`. The socket event the write produces comes back through the group
 * service and updates this list.
 *
 * ── G6 — WHICH FILTERS ARE LEGAL, AND WHY THAT IS NOT A STYLE CHOICE ─────────────────────
 *
 * Hospital, category, severity, status and date range are PLAINTEXT columns (D10). `issue`,
 * `quote` and `notes` are encrypted at rest with a random IV per write, so they can never be
 * matched or grouped in SQL (`lessons.md` §17) — two rows with identical issue text do not even
 * have the same bytes. Every dropdown below therefore reads a plaintext dimension, and the only
 * thing that touches `issue`/`notes` is the free-text search in `applyFilter`, which runs HERE,
 * over rows the model has already decrypted. Nothing in this component builds a server-side query.
 *
 * ── NULL IS EVERYWHERE, AND ALL OF IT IS LEGITIMATE ──────────────────────────────────────
 *
 * ⚠ ONE OF THOSE NULLS CHANGED MEANING ON 2026-09-01, AND THE CODE DID NOT.
 *
 * > *"patientId should never be null (enforced by the workflows and the UI, not needed on the db
 * > layer)"*
 *
 * All three creation paths now supply a patient, so a NULL `patientId` is a LEGACY SHAPE — rows
 * written before that decision — rather than a supported outcome for anything new. The column is
 * still nullable, by decision: there is NO migration and NO server-side rejection.
 *
 * EVERY `patientId` GUARD IN THIS FILE THEREFORE STAYS, and none of them is dead code. "No new row
 * can look like this" is not "no row looks like this", and this register is precisely the screen
 * that has to render the old ones. Each guard below says which of the two it is defending.
 *
 * `patientId` is nullable (§5c) — most facility issues named no patient. `hospitalId` is nullable
 * while `hospitalName` is set, routinely: §4 branch 3 matches an admission-history hospital NAME
 * against `Hospitals` exactly, and workstream D measured real misses like `Olivedale` against
 * `Netcare Olivedale`. Those rows keep their name and are grouped by it. And `location: 'rooms'`
 * is a FIRST-CLASS OUTCOME (D6) meaning an in-rooms/OPD issue, rendered "In-rooms / OPD" — never
 * "unresolved", never "Unknown".
 */
@Component({
  selector: 'app-risk-management-list',
  templateUrl: './risk-management-list.component.html',
  styleUrls: ['./risk-management-list.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0', overflow: 'hidden', opacity: '0' })),
      state('expanded', style({ height: '*', opacity: '1' })),
      transition('expanded <=> collapsed', animate('300ms ease-out')),
    ])
  ]
})
export class RiskManagementListComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() filterValue: string = '';
  /**
   * The two menu panels, handed to the parent toolbar.
   *
   * `matMenuTriggerFor` binds to a `MatMenu` INSTANCE, not a template ref, so the trigger can sit
   * in `patient-filters-list` while the panel and every control inside it stay here. That keeps
   * all filter and export state in the component that owns the register — the alternative,
   * lifting six filters and the redaction toggle into a generic five-tab host, would put
   * risk-specific state somewhere no other tab needs it.
   *
   * Bindings inside the panels still evaluate in THIS component's context, so the
   * `$event.stopPropagation()` guards and `handleMenuKeydown` continue to work unchanged.
   */
  @ViewChild('filterMenu') filterMenu!: MatMenu;
  @ViewChild('exportMenu') exportMenu!: MatMenu;


  riskItems: any[] = [];
  filteredItems: any[] = [];
  sections: RiskHospitalSection[] = [];

  /** D9. `Open → Reported → Resolved / Dismissed`, and `Open` again is always allowed. */
  /** D9, BY IMPORT — see `risk-suggestion.model.ts`. Never restate the four values. */
  readonly statusOptions: readonly string[] = RISK_STATUSES;
  categoryOptions: string[] = [...RISK_CATEGORIES];
  severityOptions: string[] = [...RISK_SEVERITIES];
  /** Built from the loaded rows, so a name-only facility (NULL `hospitalId`) is selectable too. */
  hospitalOptions: { key: string; title: string }[] = [];

  // ── G6 filter state. Every one of these is a plaintext column. ──────────────────────────
  hospitalFilter: string = '';
  categoryFilter: string = '';
  severityFilter: string = '';
  statusFilter: string = '';
  startDate: any = null;
  endDate: any = null;

  /**
   * H2 / H8 — REDACTED BY DEFAULT.
   *
   * Initialised `true`, and the service reads it through `shouldRedactNames`, which treats
   * `undefined` as redacted as well. Two independent layers, because the failure mode is a
   * privacy leak onto a document that has already left the practice.
   *
   * The label in the template is "Redact patient names" and must stay that. §6.H: this is
   * PSEUDONYMISATION — a row still carries a hospital, a ward, a date and a patient number and is
   * re-identifiable by design, so calling it "Anonymise" would be a false claim to the doctor
   * deciding whether to hand the document over.
   */
  redactNames: boolean = true;

  /**
   * D16 / §28 P7 — PHOTOGRAPHS ARE EXCLUDED FROM THE EXPORT BY DEFAULT.
   *
   * Initialised `false`, and the service treats anything other than an explicit `true` as excluded
   * — two independent layers, the same belt-and-braces shape `redactNames` uses, because the
   * failure mode is identical: a privacy leak onto a document that has already left the practice.
   *
   * ── WHY THIS IS OFF AND `redactNames` IS ON ──────────────────────────────────────────
   *
   * They are the same decision pointing the same way. D3 masks patient names because the export
   * goes to a hospital on the practice's letterhead. A ward photograph can incidentally carry a
   * wristband, a chart, a whiteboard or a face — at which point the masking is theatre and a JPEG
   * has walked straight through the privacy control we just applied to the text beside it.
   *
   * So the safe thing is the default and the doctor opts in, ALL-OR-NOTHING rather than per-image.
   * A per-image chooser would be a twenty-tap review of thumbnails on a phone, and the failure
   * mode of a review like that is that it gets skipped, which is worse than a single deliberate
   * decision the doctor takes once and can see on the document afterwards.
   *
   * DO NOT "IMPROVE" THIS TO DEFAULT ON. When it is off the PDF states how many images it withheld
   * (see `exportRiskRegisterPdf`), so the omission is visible rather than silent — that printed
   * count is the other half of this decision and must not be removed either.
   */
  includeImages: boolean = false;

  exporting = false;

  expandedHospital: string | null = null;

  constructor(
    private riskItemService: RiskItemService,
    private patientRiskItemService: PatientRiskItemService,
    private patientService: PatientService,
    private router: Router,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.riskItemService.riskItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.riskItems = items || [];
        this.rebuildHospitalOptions();
        this.applyFilter();
      });

    // THE `editingRiskItem$` SUBSCRIPTION THAT USED TO SIT HERE IS REMOVED, and it had to be.
    //
    // It made this component the listener on the hand-off channel: anything that called
    // `setEditingRiskItem` while the register was mounted got the read-only detail DIALOG, and the
    // value was cleared straight afterwards. Workstream N gave that channel its real consumer —
    // `patient-risk-detail`, fed exactly as `patient-task-detail` is fed by `editingTask$`.
    //
    // Leaving both in place would have broken N5 outright rather than merely duplicating it:
    // `loadRiskItem` below sets the item and then navigates, this component is still mounted at
    // that instant, so the old subscription would have opened the dialog AND reset the channel to
    // null — leaving the page it navigated to with nothing to edit.
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterValue']) {
      this.applyFilter();
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════════════════
  //  G6 — FILTERING
  // ═════════════════════════════════════════════════════════════════════════════════════════

  applyFilter() {
    const items = this.riskItems || [];

    // ── DATE RANGE, COMPARED AS CALENDAR DAYS ──────────────────────────────────────────────
    //
    // Both sides are reduced to 'YYYY-MM-DD' and compared as STRINGS, which is lexicographic and
    // therefore chronological. Do NOT go back to comparing moments.
    //
    // The datepicker is configured `{ useUtc: true }` (`core.module.ts:215`,
    // `patient.module.ts:431`), so picking 31 Aug yields 2026-08-31T00:00:00Z. `dateOfIssue` is
    // DATEONLY and arrives as the string '2026-08-31', which `moment()` parses in LOCAL time —
    // 2026-08-30T22:00:00Z at UTC+2. Comparing those two instants made an item ON the selected
    // day test as BEFORE it, and a 'from today' filter emptied the register.
    //
    // Same reasoning as `riskGroupKey`, which reads the day off the string rather than through a
    // Date for exactly this reason.
    const startDay = this.startDate ? moment(this.startDate).format('YYYY-MM-DD') : null;
    const endDay = this.endDate ? moment(this.endDate).format('YYYY-MM-DD') : null;
    const filterTerms = (this.filterValue || '').toLowerCase().split(' ').filter(term => !!term);

    this.filteredItems = items.filter(item => {
      if (!item) return false;

      // ── Plaintext dimensions (D10) ─────────────────────────────────────────────────────
      if (this.hospitalFilter && this.hospitalGroupKey(item) !== this.hospitalFilter) return false;
      if (this.categoryFilter && String(item.category || '').toLowerCase() !== this.categoryFilter) return false;
      if (this.severityFilter && String(item.severity || '').toLowerCase() !== this.severityFilter) return false;
      if (this.statusFilter && item.status !== this.statusFilter) return false;

      if (startDay || endDay) {
        // `dateOfIssue` is DATEONLY — the calendar day at the facility, not a timestamp (§5c). A
        // row with no date cannot satisfy a date range and is excluded rather than assumed.
        if (!item.dateOfIssue) return false;
        // Already 'YYYY-MM-DD'; sliced defensively in case a caller ever hands over a full ISO
        // instant, which would otherwise sort wrong against a bare day.
        const day = String(item.dateOfIssue).slice(0, 10);
        if (day.length !== 10) return false;
        // Inclusive at BOTH ends: picking the same day for from and to must return that day.
        if (startDay && day < startDay) return false;
        if (endDay && day > endDay) return false;
      }

      if (!filterTerms.length) return true;

      // ── Free text, CLIENT-SIDE ONLY ────────────────────────────────────────────────────
      // `issue` and `notes` are decrypted by the model on the way out and are searchable here and
      // nowhere else. This is the one place they may be matched; see the class header.
      const haystack = [
        item.Patient?.name,
        item.Patient?.surname,
        item.Patient?.patientNumber,
        item.hospitalName,
        item.hospital?.name,
        item.wardName,
        riskLocationLabel(item.location),
        riskCategoryLabel(item.category),
        riskSeverityLabel(item.severity),
        item.status,
        item.issue,
        item.notes,
        item.createdBy,
      ].filter(value => !!value).join(' ').toLowerCase();

      return filterTerms.every(term => haystack.indexOf(term) !== -1);
    });

    this.buildSections();
  }

  /** Re-runs the filter after a dropdown or datepicker change. */
  onFilterChange() {
    this.applyFilter();
  }

  clearFilters() {
    this.hospitalFilter = '';
    this.categoryFilter = '';
    this.severityFilter = '';
    this.statusFilter = '';
    this.startDate = null;
    this.endDate = null;
    this.applyFilter();
  }

  /**
   * How many of the six filters are actually narrowing the list - shown on the Filters trigger so
   * a collapsed menu cannot hide the fact that the register is filtered. A count that disagreed
   * with `hasActiveFilters` would be worse than none, so both read the same six fields.
   */
  get activeFilterCount(): number {
    return [
      this.hospitalFilter,
      this.categoryFilter,
      this.severityFilter,
      this.statusFilter,
      this.startDate,
      this.endDate,
    ].filter((value) => !!value).length;
  }

  /**
   * Keeps Tab inside an open `mat-menu` instead of letting the menu swallow it and close.
   * Same helper, same reason, as `patient-ai-request-detail.component.ts:4231`.
   */
  handleMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }

  get hasActiveFilters(): boolean {
    return !!(this.hospitalFilter || this.categoryFilter || this.severityFilter || this.statusFilter || this.startDate || this.endDate);
  }

  private rebuildHospitalOptions() {
    this.hospitalOptions = groupRiskItemsByHospital(this.riskItems).map(group => ({
      key: group.key,
      title: group.title,
    }));
    // A facility that has dropped out of the data must not stay selected and silently show an
    // empty register.
    if (this.hospitalFilter && !this.hospitalOptions.some(option => option.key === this.hospitalFilter)) {
      this.hospitalFilter = '';
    }
  }

  /** Hospital first, then category — the PDF's shape (H3), built from the same helper. */
  private buildSections() {
    this.sections = groupRiskItemsByHospital(this.filteredItems).map(group => {
      const byCategory = new Map<string, any[]>();
      for (const item of group.items) {
        const category = String(item.category || '').toLowerCase() || 'uncategorised';
        const bucket = byCategory.get(category);
        if (bucket) {
          bucket.push(item);
        } else {
          byCategory.set(category, [item]);
        }
      }

      const categories: RiskCategorySection[] = [];
      // Taxonomy order (D7) first, so a facility's sections read the same shape month to month.
      for (const category of RISK_CATEGORIES) {
        const bucket = byCategory.get(category);
        if (bucket) {
          categories.push({ category, label: riskCategoryLabel(category), items: this.sortItems(bucket) });
          byCategory.delete(category);
        }
      }
      // Anything off-taxonomy is still shown rather than lost.
      for (const [category, bucket] of byCategory) {
        categories.push({ category, label: riskCategoryLabel(category), items: this.sortItems(bucket) });
      }

      return {
        key: group.key,
        title: group.title,
        nameOnly: group.nameOnly,
        summary: riskCategorySummary(group.items),
        total: group.items.length,
        categories,
      };
    });

    // Keep an open section open across a refilter; otherwise open the first.
    if (!this.expandedHospital || !this.sections.some(section => section.key === this.expandedHospital)) {
      this.expandedHospital = this.sections.length ? this.sections[0].key : null;
    }
  }

  private sortItems(items: any[]): any[] {
    return [...items].sort((a, b) => String(b.dateOfIssue || '').localeCompare(String(a.dateOfIssue || '')));
  }

  toggleHospital(key: string) {
    this.expandedHospital = this.expandedHospital === key ? null : key;
  }

  // ═════════════════════════════════════════════════════════════════════════════════════════
  //  LABELS — the shared vocabulary, never restated
  // ═════════════════════════════════════════════════════════════════════════════════════════

  categoryLabel(category?: string): string {
    return riskCategoryLabel(category);
  }

  severityLabel(severity?: string): string {
    return riskSeverityLabel(severity);
  }

  locationLabel(location?: string | null): string {
    return riskLocationLabel(location);
  }

  /**
   * Delegates to the service. The register on screen and the tables in the exported PDF MUST agree
   * about which rows belong to which facility — in particular about the rows whose `hospitalId` is
   * NULL while `hospitalName` is set — so there is exactly one implementation of this and it is
   * the one the export uses.
   */
  hospitalGroupKey(item: any): string {
    return riskHospitalGroupKey(item);
  }

  /**
   * The Patient cell. LEGACY-SHAPE GUARD (see the class header) — every item created since
   * 2026-09-01 names a patient, but rows written before that do not, and this cell is what renders
   * them. Not dead code, and not to be tidied away.
   *
   * The fallback also covers a second, still-current case that has nothing to do with the
   * decision: a row whose `patientId` IS set but whose `Patient` include came back empty, which is
   * what a `required: false` LEFT JOIN yields for a patient row that has since been removed.
   */
  /**
   * Two DIFFERENT facts, which this used to collapse into one sentence.
   *
   *   patientId null           -> genuinely not about a patient. LEGACY SHAPE: the column is
   *                               still nullable, but every creation path now supplies a patient
   *                               (user decision, 2026-09-01), so new rows never look like this.
   *   patientId set, Patient   -> the association was not loaded on this payload. NOT the same
   *   association absent          claim, and saying "no patient" about it is simply wrong.
   *
   * This is not hypothetical. `markItemsReported` used to emit rows without their includes, and
   * because the client REPLACES a row on `riskItemUpdated` rather than merging, every exported
   * item rendered as "No patient" — a real bug reported as data loss. The include is fixed, but
   * the label must not be able to make that claim again on a thin payload.
   */
  patientLabel(item: any): string {
    if (!item?.Patient) {
      return item?.patientId ? 'Patient not loaded' : 'No patient';
    }
    const surname = item.Patient.surname || '';
    const name = item.Patient.name || '';
    const label = `${surname}${surname && name ? ', ' : ''}${name}`.trim();
    return label || (item.patientId ? 'Patient not loaded' : 'No patient');
  }

  // ═════════════════════════════════════════════════════════════════════════════════════════
  //  G5 — STATUS TRANSITIONS, INLINE
  // ═════════════════════════════════════════════════════════════════════════════════════════

  /**
   * D9's lifecycle. `Reported` is stamped server-side with `reportedAt` if it is not already set —
   * one meaning of "already sent", whether it came from here or from the export (H5).
   *
   * `Resolved` and `Dismissed` are confirmed, following `task-list.component.ts:130`'s reasoning:
   * they change what a monthly export contains, and an accidental click while scrolling silently
   * drops an issue the practice meant to raise. Moving an item back to `Open` is reversible and
   * stays a single click.
   */
  async updateStatus(event: MouseEvent, item: any, status: string) {
    event.preventDefault();
    event.stopPropagation();

    const id = item?.id;
    if (!id || item.status === status) return;

    if (status === 'Resolved' || status === 'Dismissed') {
      const confirmed = await this.showConfirmationDialog(
        'Confirm Risk Item Status',
        `Are you sure you want to mark this issue as ${status}?`,
        item?.issue ? [{ patient: item.issue }] : undefined,
        'No',
        'Yes'
      );
      if (!confirmed) return;
    }

    this.patientRiskItemService.updateRiskItem(id, { status })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showToastMessage(`Risk item marked as ${status}`);
        },
        error: (error) => {
          console.error('Error updating risk item status: ', error);
          this.showToastMessage('There was an error updating the risk item. Please try again.');
        }
      });
  }

  // ═════════════════════════════════════════════════════════════════════════════════════════
  //  THE DETAIL VIEW — the click-through workstream J had nowhere to send
  // ═════════════════════════════════════════════════════════════════════════════════════════

  /**
   * N5 — a register row opens the routed detail page, following `task-list.loadTask` exactly:
   * load the patient, set the editing row, navigate into the patient outlet. The patient must be
   * loaded FIRST — `patient-risk-detail` reads `currentPatient$` for the id it saves against and
   * for `returnToParent`, and the register can be showing rows from any patient in the practice.
   *
   * ── THE ONE CASE `loadTask` DOES NOT HAVE ────────────────────────────────────────────────
   *
   * `PatientRiskItem.patientId` IS NULLABLE (§5c), and rows written before 2026-09-01 name no
   * patient at all ("pharmacy had no ceftriaxone"). There is no patient file to open for those and
   * no `edit-risk-item` route outside one — that route lives in the `patient` outlet and
   * `patient-risk-detail` reads `currentPatient$` for the id it saves against — so they keep G's
   * read-only detail dialog.
   *
   * ⚠ LEGACY-SHAPE GUARD, KEPT DELIBERATELY (see the class header). New items always name a
   * patient, so this branch is unreachable for anything created from here on — and it is still the
   * ONLY correct answer for the rows that already exist. Removing it would not simplify this
   * method; it would make an old row navigate into a patient file that is not there. It is why
   * `risk-item-detail-dialog` is still wired up here and must stay wired up.
   */
  openRiskItem(item: any) {
    if (!item) return;

    if (item.patientId && item.id) {
      this.loadRiskItem(item);
      return;
    }

    this.dialog.open(RiskItemDetailDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      data: { item },
    });
  }

  private loadRiskItem(item: any) {
    this.patientService.getPatientById(item.patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Set, THEN navigate — `patient-risk-detail` reads the row off `editingRiskItem$` as it
          // initialises. This is the same order `task-list.loadTask` uses.
          this.patientRiskItemService.setEditingRiskItem(item);
          this.router.navigate(['/patients', {
            outlets: {
              patient: ['edit-risk-item', item.id],
            }
          }]);
        },
        error: (error) => {
          console.error(error);
          this.showToastMessage('There was an error loading the risk item for editing. Please try again.');
        }
      });
  }

  navigateToPatientFile(event: MouseEvent, patientId: string | null) {
    event.preventDefault();
    event.stopPropagation();

    // NULL is normal here (§5c). The button is hidden for those rows; this is the second guard.
    if (!patientId) return;

    this.patientService.getPatientById(patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.router.navigate(['/patients', {
            outlets: {
              patient: ['file', patientId],
            }
          }]);
        },
        error: (error) => {
          console.error(error);
          this.showToastMessage('There was an error loading the patient file. Please try again.');
        }
      });
  }

  // ═════════════════════════════════════════════════════════════════════════════════════════
  //  H — EXPORT
  // ═════════════════════════════════════════════════════════════════════════════════════════

  /**
   * H1-H5. Exports EXACTLY what the register currently shows — the filtered rows, in the same
   * hospital grouping — then offers to mark those rows `Reported` (H5).
   *
   * The offer is made AFTER the download, and only for the ids the document actually covered. It
   * is an offer and not an automatic write: a doctor generating a draft to look at has not sent
   * anything to a hospital, and `reportedAt` is the one fact §7 keeps instead of storing the PDF.
   */
  async exportRegister() {
    if (this.exporting) return;

    const items = this.filteredItems || [];
    if (!items.length) {
      this.showToastMessage('There are no risk items to export.');
      return;
    }

    this.exporting = true;
    try {
      const ids = await this.riskItemService.exportRiskRegisterPdf(items, {
        redactNames: this.redactNames,
        // D16 / §28 P7. Default `false`; when it stays false the document PRINTS the number of
        // images it withheld rather than omitting them silently.
        includeImages: this.includeImages,
        periodLabel: this.periodLabel(),
        filterSummary: this.filterSummary(),
      });

      if (!ids.length) return;

      // A row whose `patientId` is set but whose `Patient` association did not come with the
      // payload prints "Not available" in the document rather than a name. That is the honest
      // cell — an em dash would tell the hospital the issue had no patient — but the doctor is
      // about to hand over a PDF with a gap in it, so they are told before they do.
      const unresolved = items.filter(item => !item.Patient && item.patientId).length;
      if (unresolved > 0) {
        this.showToastMessage(
          unresolved + ' row(s) could not show a patient name on the export. Refresh the register'
          + ' and export again if the document needs them.'
        );
      }

      // Counted over the rows that actually carry an id — the same set the export returned — so the
      // number quoted to the doctor is the number the write can actually touch.
      const outstanding = items.filter(item => item.id && item.status !== 'Reported').length;

      if (outstanding <= 0) {
        this.showToastMessage('Register exported. Every item on it was already marked as reported.');
        return;
      }

      const confirmed = await this.showConfirmationDialog(
        'Mark these items as reported?',
        `The register you just downloaded covers ${ids.length} item${ids.length === 1 ? '' : 's'}, ${outstanding} of which ${outstanding === 1 ? 'is' : 'are'} not yet marked as reported. Marking them records the date they were sent, so next month's register does not raise them again. Nothing about the PDF itself is stored.`,
        undefined,
        'Not now',
        'Mark as reported'
      );

      if (!confirmed) return;

      this.riskItemService.markItemsReported(ids)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            // The server reports how many of the requested ids it actually marked; ids naming
            // another practice's rows or deleted rows are silently absent. Show what it said
            // rather than assuming requested === marked.
            this.showToastMessage(result?.message || 'Risk items marked as reported.');
          },
          error: (error) => {
            console.error('Error marking risk items as reported: ', error);
            this.showToastMessage('The register downloaded, but the items could not be marked as reported. Please try again.');
          }
        });
    } catch (error) {
      console.error('Error exporting risk register: ', error);
      this.showToastMessage('There was an error generating the risk register. Please try again.');
    } finally {
      this.exporting = false;
    }
  }

  /** The date range as it will read under the title of the exported document. */
  periodLabel(): string {
    if (this.startDate && this.endDate) {
      return `${moment(this.startDate).format('DD/MM/YYYY')} to ${moment(this.endDate).format('DD/MM/YYYY')}`;
    }
    if (this.startDate) return `From ${moment(this.startDate).format('DD/MM/YYYY')}`;
    if (this.endDate) return `To ${moment(this.endDate).format('DD/MM/YYYY')}`;
    return 'All dates';
  }

  /**
   * The active filters, printed on the document. A hospital reading a register needs to know it is
   * a filtered view; an unfiltered-looking document that silently omitted every `high` severity
   * row would misrepresent the practice's own data.
   */
  filterSummary(): string {
    const parts: string[] = [];
    if (this.hospitalFilter) {
      const option = this.hospitalOptions.find(candidate => candidate.key === this.hospitalFilter);
      if (option) parts.push(`Facility: ${option.title}`);
    }
    if (this.categoryFilter) parts.push(`Category: ${riskCategoryLabel(this.categoryFilter)}`);
    if (this.severityFilter) parts.push(`Severity: ${riskSeverityLabel(this.severityFilter)}`);
    if (this.statusFilter) parts.push(`Status: ${this.statusFilter}`);
    if (this.filterValue) parts.push(`Search: "${this.filterValue}"`);
    return parts.join('  ·  ');
  }
}
